import { useTranslation } from 'react-i18next';

import { groupMissions, type MissionGroup } from '@/lib/missions/model';
import type { MissionView } from '@/types/quest';

import { MissionRow } from './MissionRow';
import { MissionsEmpty } from './MissionsEmpty';

interface MissionsListProps {
  missions: readonly MissionView[];
  now: Date;
}

/**
 * L'ordine in cui i gruppi compaiono.
 *
 * In mezzo restano le tre viste di Luca nel suo ordine — *«tutte le quest aperte, quelle in fase di
 * avanzamento utente e quelle completate»* — con «Completate» che ora contiene esattamente cio' che
 * dice: le riscosse.
 *
 * 🔴 «DA RISCUOTERE» STA IN CIMA, ED E' L'UNICO POSTO ONESTO. E' il solo gruppo che contiene
 * un'AZIONE, e questo lavoro nasce da un utente (Luca, dal telefono) che ha letto «da riscuotere» e
 * non ha trovato dove riscuotere. Metterlo terzo lo renderebbe di nuovo qualcosa da cercare. Il
 * `RANK` di `model.ts` diceva gia' `completed: 0` — «la sola riga su cui l'utente puo' agire» —
 * ma ordinava DENTRO i gruppi: la sezione lo rendeva invisibile, e ora non piu'.
 */
const ORDER: readonly MissionGroup[] = ['claimable', 'open', 'inProgress', 'claimed', 'closed'];

/**
 * I gruppi che restano visibili anche VUOTI: le tre viste di Luca, e solo quelle.
 *
 * Sparire li farebbe saltare su e giu' a ogni refetch, sotto le dita di chi guarda, e toglierebbe
 * la mappa di dove stanno le cose. Gli altri due si nascondono quando sono vuoti, per ragioni
 * opposte e simmetriche: `closed` e' l'avanzo, e una sezione «Non piu' disponibili — nessuna» e'
 * rumore; `claimable` e' in cima, cioe' sullo spazio piu' prezioso della pagina, e un «Nulla da
 * riscuotere» permanente sarebbe il rumore piu' costoso disponibile — oltre a essere, per la quasi
 * totalita' degli utenti e per la quasi totalita' del tempo, l'unica cosa che si vedrebbe. Che
 * COMPAIA quando arriva un premio non e' un difetto: e' il salto che si vuole notare.
 */
const ALWAYS_VISIBLE: readonly MissionGroup[] = ['open', 'inProgress', 'claimed'];

/**
 * Le tre viste, piu' cio' che si riscuote e la coda. Presentazionale di proposito — riceve i dati, non li va a prendere:
 * cosi' si puo' rendere contro risposte finte (lista vuota, 403, 410, 503) senza rete, che e'
 * l'unico modo di provarla finche' non esistono l'utente Logto di test e un giocatore con missioni.
 *
 * ⚠️ SEZIONI IN UNA PAGINA, NON TAB. Con le tab lo stato vuoto va scritto tre volte e visto
 * UNA ALLA VOLTA — e lo stato vuoto e' la schermata che si vedra' per prima e a lungo (Quest e'
 * inerte). Un giocatore che aprisse una sola tab vuota non saprebbe se e' vuota lei o e' vuoto
 * tutto. Inoltre il vocabolario `Segmented`/`UnderlineTabs` non esiste su questo `main`: usarlo
 * significherebbe inventarlo.
 */
export function MissionsList({ missions, now }: MissionsListProps) {
  // Vuoto GLOBALE: nessuna missione in assoluto. E' uno stato diverso da "nessuna in questo
  // gruppo", e oggi e' lo stato normale della sezione — merita la schermata scritta, non tre
  // sezioni vuote una sotto l'altra che dicono tre volte la stessa cosa.
  if (missions.length === 0) return <MissionsEmpty />;

  const groups = groupMissions(missions, now);

  return (
    <div className="flex flex-col gap-6">
      {ORDER.map((group) =>
        !ALWAYS_VISIBLE.includes(group) && groups[group].length === 0 ? null : (
          <MissionsGroup key={group} group={group} missions={groups[group]} now={now} />
        ),
      )}
    </div>
  );
}

function MissionsGroup({
  group,
  missions,
  now,
}: {
  group: MissionGroup;
  missions: readonly MissionView[];
  now: Date;
}) {
  const { t } = useTranslation('missions');
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
        {t(`group.${group}.title`)}
      </h2>
      {missions.length === 0 ? (
        // Vuoto DI GRUPPO: sobrio, una riga. Dice "qui no, altrove si'", che e' informazione.
        <p className="rounded-xl bg-bg-secondary px-3 py-4 text-center text-xs text-fg-muted">
          {t(`group.${group}.empty`)}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {missions.map((mission) => (
            <MissionRow key={mission.instanceId} mission={mission} now={now} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** Il caricamento: tre segnaposto della stessa forma delle righe, per non far saltare il layout. */
export function MissionsLoading() {
  return (
    <ul className="flex flex-col gap-2" aria-busy="true">
      {[0, 1, 2].map((i) => (
        <li key={i} className="h-24 animate-pulse rounded-xl bg-bg-secondary" />
      ))}
    </ul>
  );
}

/**
 * L'errore. Distinto dal vuoto di proposito: "non hai missioni" e "non siamo riusciti a
 * caricarle" sono due cose diverse, e confonderle e' esattamente il modo in cui questa
 * integrazione fallirebbe in silenzio per settimane. Con TRE gruppi la difesa conta di piu', non
 * di meno: tre sezioni vuote sono tre modi di sembrare a posto mentre non lo si e'.
 */
export function MissionsError({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation('missions');
  return (
    <div className="rounded-xl bg-bg-secondary p-6 text-center">
      <p className="text-sm font-bold text-fg">{t('error.title')}</p>
      <p className="mt-1 text-sm text-fg-muted">{t('error.text')}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 w-full rounded-xl bg-bg-tertiary py-3 text-sm font-bold text-fg-muted transition-opacity active:opacity-80"
        >
          {t('error.retry')}
        </button>
      )}
    </div>
  );
}
